import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Medal } from 'lucide-react';
import { createCategorySlug } from '../../utils/urlHelpers';
import { formatLeaderValue, type LeaderMetric, type RankedForum } from '../../utils/leaderboard';

interface Props {
  topThree: RankedForum[];
  metric: LeaderMetric;
  darkMode: boolean;
}

const slotConfig = [
  { place: 2, height: 'h-28 sm:h-32', medal: 'from-slate-300 to-slate-500 text-slate-900', col: 'col-start-1' },
  { place: 1, height: 'h-36 sm:h-44', medal: 'from-amber-400 to-amber-600 text-amber-950', col: 'col-start-2' },
  { place: 3, height: 'h-24 sm:h-28', medal: 'from-amber-700 to-amber-900 text-amber-100', col: 'col-start-3' },
] as const;

const LeaderboardPodium: React.FC<Props> = ({ topThree, metric, darkMode }) => {
  if (topThree.length === 0) return null;

  const byRank = new Map(topThree.map((e) => [e.rank, e]));

  return (
    <section
      className={`rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 ${
        darkMode
          ? 'bg-gradient-to-b from-indigo-950/80 to-gray-900 border border-indigo-900/50'
          : 'bg-gradient-to-b from-indigo-50 to-white border border-indigo-100 shadow-sm'
      }`}
      aria-label="Podyum — ilk üç"
    >
      <h2
        className={`text-center text-sm font-semibold uppercase tracking-wider mb-4 sm:mb-6 ${
          darkMode ? 'text-indigo-300' : 'text-indigo-700'
        }`}
      >
        Podyum
      </h2>
      <div className="grid grid-cols-3 gap-2 sm:gap-4 items-end max-w-2xl mx-auto">
        {slotConfig.map(({ place, height, medal, col }) => {
          const entry = byRank.get(place);
          if (!entry) {
            return <div key={place} className={col} />;
          }
          const isFirst = place === 1;

          return (
            <div key={entry.forum.id} className={`flex flex-col items-center min-w-0 ${col}`}>
              <Link
                to={`/forum/${entry.forum.forum_id}`}
                className={`flex flex-col items-center w-full group mb-2 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {entry.forum.logo_url ? (
                  <img
                    src={entry.forum.logo_url}
                    alt=""
                    className={`object-contain rounded-xl border-2 mb-2 ${
                      isFirst ? 'w-14 h-14 sm:w-20 sm:h-20' : 'w-12 h-12 sm:w-16 sm:h-16'
                    } ${darkMode ? 'border-indigo-700 bg-gray-800' : 'border-indigo-200 bg-white'}`}
                  />
                ) : (
                  <div
                    className={`rounded-xl flex items-center justify-center font-bold mb-2 bg-gradient-to-br ${medal} ${
                      isFirst ? 'w-14 h-14 sm:w-20 sm:h-20 text-lg sm:text-xl' : 'w-12 h-12 sm:w-16 sm:h-16 text-base'
                    }`}
                  >
                    {place}
                  </div>
                )}
                {isFirst && <Crown className="text-amber-400 mb-1" size={20} aria-hidden />}
                <span
                  className={`font-semibold text-center line-clamp-2 leading-tight group-hover:underline ${
                    isFirst ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'
                  }`}
                >
                  {entry.forum.name}
                </span>
                <span
                  className={`text-xs mt-0.5 truncate max-w-full ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {entry.forum.category}
                </span>
              </Link>
              <div
                className={`w-full rounded-t-xl flex flex-col items-center justify-end pb-2 pt-3 bg-gradient-to-t ${medal} ${height}`}
              >
                <Medal size={isFirst ? 22 : 18} className="opacity-90 mb-1" aria-hidden />
                <span className="text-xs sm:text-sm font-bold tabular-nums">
                  {formatLeaderValue(metric, entry.value, entry.growth)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap justify-center gap-2 mt-4">
        {topThree.map((e) => (
          <Link
            key={e.forum.id}
            to={`/category/${createCategorySlug(e.forum.category)}`}
            className={`text-xs px-2 py-1 rounded-full ${
              darkMode
                ? 'bg-gray-800 text-indigo-300 hover:bg-gray-700'
                : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            }`}
          >
            {e.forum.category}
          </Link>
        ))}
      </div>
    </section>
  );
};

export default LeaderboardPodium;
